import React, { useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { CalendarToday, Refresh } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { getDateRangePresets } from '../../../utils/reportHelpers';

export interface ReportFiltersProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  siteId?: string;
  onSiteIdChange?: (siteId: string) => void;
  vendorId?: string;
  onVendorIdChange?: (vendorId: string) => void;
  chargerId?: string;
  onChargerIdChange?: (chargerId: string) => void;
  groupBy?: string;
  onGroupByChange?: (groupBy: string) => void;
  onRefresh?: () => void;
  showSiteFilter?: boolean;
  showVendorFilter?: boolean;
  showChargerFilter?: boolean;
  showGroupByFilter?: boolean;
  sites?: Array<{ id: string; name: string }>;
  vendors?: Array<{ id: string; name: string }>;
  chargers?: Array<{ id: string; name: string }>;
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  siteId = '',
  onSiteIdChange,
  vendorId = '',
  onVendorIdChange,
  chargerId = '',
  onChargerIdChange,
  groupBy = 'day',
  onGroupByChange,
  onRefresh,
  showSiteFilter = false,
  showVendorFilter = false,
  showChargerFilter = false,
  showGroupByFilter = false,
  sites = [],
  vendors = [],
  chargers = [],
}) => {
  const translate = useTranslate();
  const presets = getDateRangePresets(translate);

  // Determine which preset is currently selected
  const selectedPreset = useMemo(() => {
    const presetEntries = Object.entries(presets);
    for (const [key, preset] of presetEntries) {
      if (preset.start === startDate && preset.end === endDate) {
        return key;
      }
    }
    return 'custom';
  }, [startDate, endDate, presets]);

  const handlePresetClick = (preset: { start: string; end: string }) => {
    onStartDateChange(preset.start);
    onEndDateChange(preset.end);
  };

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      {/* Date Range Presets */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {Object.entries(presets).map(([key, preset]) => {
          const isSelected = selectedPreset === key;
          return (
            <Chip
              key={key}
              label={preset.label}
              onClick={() => handlePresetClick(preset)}
              color={isSelected ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                backgroundColor: isSelected ? '#009A75' : 'default',
                color: isSelected ? '#FFFFFF' : 'default',
                fontWeight: isSelected ? 600 : 400,
                '&:hover': {
                  backgroundColor: isSelected ? '#008060' : '#009A7515',
                  color: isSelected ? '#FFFFFF' : '#009A75',
                },
              }}
            />
          );
        })}
        {/* Custom preset chip */}
        <Chip
          label={translate('reports.filters.custom', { fallback: 'Custom' })}
          color={selectedPreset === 'custom' ? 'primary' : 'default'}
          sx={{
            cursor: 'default',
            backgroundColor: selectedPreset === 'custom' ? '#009A75' : 'default',
            color: selectedPreset === 'custom' ? '#FFFFFF' : 'default',
            fontWeight: selectedPreset === 'custom' ? 600 : 400,
          }}
        />
      </Box>

      {/* Filter Controls */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* Start Date */}
        <TextField
          label={translate('reports.filters.startDate', { fallback: 'Start Date' })}
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          size="small"
        />

        {/* End Date */}
        <TextField
          label={translate('reports.filters.endDate', { fallback: 'End Date' })}
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          size="small"
        />

        {/* Vendor Filter */}
        {showVendorFilter && onVendorIdChange && (
          <FormControl sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel>{translate('reports.filters.vendor', { fallback: 'Vendor' })}</InputLabel>
            <Select
              value={vendorId}
              onChange={(e: SelectChangeEvent) => onVendorIdChange(e.target.value)}
              label={translate('reports.filters.vendor', { fallback: 'Vendor' })}
            >
              <MenuItem value="">
                {translate('reports.filters.allVendors', { fallback: 'All Vendors' })}
              </MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Site Filter */}
        {showSiteFilter && onSiteIdChange && (
          <FormControl sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel>{translate('reports.filters.site', { fallback: 'Site' })}</InputLabel>
            <Select
              value={siteId}
              onChange={(e: SelectChangeEvent) => onSiteIdChange(e.target.value)}
              label={translate('reports.filters.site', { fallback: 'Site' })}
            >
              <MenuItem value="">
                {translate('reports.filters.allSites', { fallback: 'All Sites' })}
              </MenuItem>
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Charger Filter */}
        {showChargerFilter && onChargerIdChange && (
          <FormControl sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel>{translate('reports.filters.charger', { fallback: 'Charger' })}</InputLabel>
            <Select
              value={chargerId}
              onChange={(e: SelectChangeEvent) => onChargerIdChange(e.target.value)}
              label={translate('reports.filters.charger', { fallback: 'Charger' })}
            >
              <MenuItem value="">
                {translate('reports.filters.allChargers', { fallback: 'All Chargers' })}
              </MenuItem>
              {chargers.map((charger) => (
                <MenuItem key={charger.id} value={charger.id}>
                  {charger.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Group By Filter */}
        {showGroupByFilter && onGroupByChange && (
          <FormControl sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} size="small">
            <InputLabel>{translate('reports.filters.groupBy', { fallback: 'Group By' })}</InputLabel>
            <Select
              value={groupBy}
              onChange={(e: SelectChangeEvent) => onGroupByChange(e.target.value)}
              label={translate('reports.filters.groupBy', { fallback: 'Group By' })}
            >
              <MenuItem value="day">{translate('reports.groupBy.day', { fallback: 'Day' })}</MenuItem>
              <MenuItem value="week">{translate('reports.groupBy.week', { fallback: 'Week' })}</MenuItem>
              <MenuItem value="month">{translate('reports.groupBy.month', { fallback: 'Month' })}</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Refresh Button */}
        {onRefresh && (
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={onRefresh}
            size="small"
            sx={{
              borderColor: '#009A75',
              color: '#009A75',
              '&:hover': {
                borderColor: '#008060',
                backgroundColor: '#009A7510',
              },
            }}
          >
            {translate('reports.actions.refresh', { fallback: 'Refresh' })}
          </Button>
        )}
      </Box>
    </Box>
  );
};
